import axios from "axios";
import { initErrorRecorder } from "@/lib/errorRecorder";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared axios instance — the Bearer token is attached once the session boots.
// A generous default timeout keeps a hung edge gateway from leaving long AI
// calls (e.g. comic generation) stuck open forever.
export const api = axios.create({ baseURL: API, timeout: 180000 });

// Auto-capture failed API calls (esp. paid generation tools) + JS crashes into
// a 48h rolling log for the founder Admin panel's Error Recorder.
initErrorRecorder(api);

// --- Graceful external-AI / rate-limit interception -------------------------
// Providers (fal, OpenAI, etc.) and our own throttle guards return HTTP 429
// under rapid generation. Rather than surfacing a raw Cloudflare HTML page or a
// cryptic error, we normalise the error into a friendly, human-readable string
// and broadcast a global cool-down event so the UI can show a polite banner.
// This NEVER swallows the error — the rejected promise still propagates so
// each caller's own catch/toast keeps working.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const resp = error?.response;
    const status = resp?.status;
    const data = resp?.data;

    // Cloudflare / gateway 5xx often return an HTML body — replace it with a
    // clean message so a raw "<!DOCTYPE html>…" never lands in a toast.
    if (typeof data === "string" && /<html|<!doctype/i.test(data)) {
      if (resp) resp.data = { detail: "The service is briefly busy. Please try again in a moment." };
    }

    if (status === 429) {
      const detail =
        (typeof resp?.data?.detail === "string" && resp.data.detail) ||
        "You're generating too fast — please wait a moment before the next one.";
      if (resp) resp.data = { detail, code: "rate_limited" };
      // Broadcast a 60s cool-down so the global banner can mask the canvas.
      try {
        window.dispatchEvent(new CustomEvent("ai-rate-limit", { detail: { message: detail, seconds: 60 } }));
      } catch { /* non-browser env */ }
    }

    if (error?.code === "ECONNABORTED") {
      if (resp) resp.data = { detail: "That request took too long. Please try again." };
    }

    return Promise.reject(error);
  }
);

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
}
